import dayjs from 'dayjs'
import useDate from 'src/CrudManager/Core/composables/useDate'
import { useUserStore } from 'src/CrudManager/Core/stores/useUserStore'
import { usePunchStore } from 'src/stores/usePunchStore'
import type { ComputedRef } from 'vue'
import { computed } from 'vue'
import { usePunchTimers } from './usePunchTimers'

// Constants
const MAX_PAID_BREAK_TIME = 30 * 60 // 30 minutes in seconds

// Types
export interface BreakItem {
  start: string
  end: string | null
  duration: string
  durationSeconds: number
}

export interface PunchCalculations {
  punchInTime: ComputedRef<string>
  punchOutTime: ComputedRef<string>
  breaks: ComputedRef<BreakItem[]>
  totalBreakSeconds: ComputedRef<number>
  paidBreakSeconds: ComputedRef<number>
  unpaidBreakSeconds: ComputedRef<number>
  totalBreakTime: ComputedRef<string>
  rawWorkSeconds: ComputedRef<number>
  billableWorkSeconds: ComputedRef<number>
  rawWorkTime: ComputedRef<string>
  billableWorkTime: ComputedRef<string>
  travelTime: ComputedRef<number>
  formatTravelTime: ComputedRef<string>
  breakStartTime: ComputedRef<string>
  isPaidBreak: (breakItem: BreakItem) => boolean
  isPartiallyPaidBreak: (breakItem: BreakItem) => boolean
  plannedTravelTime: ComputedRef<string>
  formatDuration: (seconds: number) => string
}

export function usePunchCalculations(): PunchCalculations {
  const punchStore = usePunchStore()
  const userStore = useUserStore()
  const { formatTimeSpent } = useDate()
  const punchTimers = usePunchTimers() 

  // Travel time in minutes from user info
  const travelTime = computed(() => {
    let travel = 0
    try {
      if (userStore.user?.infos && 'travel_time' in userStore.user.infos) {
        travel = Number(userStore.user.infos.travel_time) || 0
      }
    } catch (e) {
      console.warn('Error reading travel time:', e)
    }
    return travel
  })

  const formatTravelTime = computed(() => {
    const hours = Math.floor(travelTime.value / 60)
    const minutes = travelTime.value % 60
    return `${hours}h ${minutes}min`
  })

  // Get the punch in time
  const punchInTime = computed(() => {
    const workDayPunches = punchStore.workToday?.punches || []
    const inPunch = workDayPunches.find((punch) => punch.type === 'IN')
    return inPunch?.punched_at || ''
  })

  // Get the punch out time
  const punchOutTime = computed(() => {
    const workDayPunches = punchStore.workToday?.punches || []
    const outPunch = workDayPunches.find((punch) => punch.type === 'OUT')
    return outPunch?.punched_at || ''
  })

  // Get all break start/end pairs
  const breaks = computed(() => {
    const workDayPunches = punchStore.workToday?.punches || []
    const breakStarts = workDayPunches
      .filter((punch) => punch.type === 'BREAK_START' && punch.punched_at !== null)
      .sort((a, b) => new Date(a.punched_at).getTime() - new Date(b.punched_at).getTime())

    const breakEnds = workDayPunches
      .filter((punch) => punch.type === 'BREAK_END' && punch.punched_at !== null)
      .sort((a, b) => new Date(a.punched_at).getTime() - new Date(b.punched_at).getTime())

    const breakPairs: BreakItem[] = []

    // Create pairs of break start and end times
    for (let i = 0; i < breakStarts.length; i++) {
      const breakStart = breakStarts[i]
      const breakEnd = i < breakEnds.length ? breakEnds[i] : null

      breakPairs.push({
        start: breakStart.punched_at,
        end: breakEnd?.punched_at || null,
        duration: breakEnd
          ? formatTimeSpent(breakStart.punched_at, breakEnd.punched_at)
          : formatTimeSpent(breakStart.punched_at, punchTimers.currentTime.value.toISOString()),
        durationSeconds: breakEnd
          ? dayjs(breakEnd.punched_at).diff(dayjs(breakStart.punched_at), 'second')
          : dayjs(punchTimers.currentTime.value).diff(dayjs(breakStart.punched_at), 'second'),
      })
    }

    return breakPairs
  })

  // Calculate total break time in seconds
  const totalBreakSeconds = computed(() => {
    if (!breaks.value.length) return 0

    return breaks.value.reduce((total, breakItem) => {
      const start = dayjs(breakItem.start)
      const end = breakItem.end ? dayjs(breakItem.end) : dayjs(punchTimers.currentTime.value)
      return total + end.diff(start, 'second')
    }, 0)
  })

  // Calculate paid and unpaid break time
  const paidBreakSeconds = computed(() => {
    let totalPaidSeconds = 0
    let remainingPaidTime = MAX_PAID_BREAK_TIME

    // Allocate paid break time to each break period in chronological order
    for (const breakItem of breaks.value) {
      if (remainingPaidTime <= 0) break

      const breakDuration = breakItem.durationSeconds
      const paidSeconds = Math.min(breakDuration, remainingPaidTime)

      totalPaidSeconds += paidSeconds
      remainingPaidTime -= paidSeconds
    }

    return totalPaidSeconds
  })

  const unpaidBreakSeconds = computed(() => {
    return totalBreakSeconds.value - paidBreakSeconds.value
  })

  // Check if a specific break is paid (fully or partially)
  const isPaidBreak = (breakItem: BreakItem) => {
    // Calculate the total duration of breaks that came before this one
    const index = breaks.value.findIndex((b) => b === breakItem)
    if (index === -1) return false

    let priorBreaksDuration = 0
    for (let i = 0; i < index; i++) {
      priorBreaksDuration += breaks.value[i].durationSeconds
    }

    // If prior breaks haven't used up any paid time, this break is fully paid
    return priorBreaksDuration < MAX_PAID_BREAK_TIME && breakItem.durationSeconds <= MAX_PAID_BREAK_TIME - priorBreaksDuration
  }

  // Check if a break is partially paid
  const isPartiallyPaidBreak = (breakItem: BreakItem) => {
    const index = breaks.value.findIndex((b) => b === breakItem)
    if (index === -1) return false

    let priorBreaksDuration = 0
    for (let i = 0; i < index; i++) {
      priorBreaksDuration += breaks.value[i].durationSeconds
    }

    // Break is partially paid if it starts within the paid time but extends beyond it
    const remainingPaidTime = MAX_PAID_BREAK_TIME - priorBreaksDuration
    return remainingPaidTime > 0 && breakItem.durationSeconds > remainingPaidTime
  }

  // Format total break time
  const totalBreakTime = computed(() => {
    return formatDuration(totalBreakSeconds.value)
  })

  // Calculate raw work time (total time from punch in to punch out/now)
  const rawWorkSeconds = computed(() => {
    if (!punchInTime.value) return 0

    const start = dayjs(punchInTime.value)
    const end = punchOutTime.value ? dayjs(punchOutTime.value) : dayjs(punchTimers.currentTime.value)
    return end.diff(start, 'second')
  })

  // Calculate billable time (total time - unpaid breaks - travel time)
  const billableWorkSeconds = computed(() => {
    return rawWorkSeconds.value - unpaidBreakSeconds.value - travelTime.value * 2 * 60
  })

  // Format raw work time
  const rawWorkTime = computed(() => {
    return formatDuration(rawWorkSeconds.value)
  })

  // Format billable work time
  const billableWorkTime = computed(() => {
    return formatDuration(billableWorkSeconds.value)
  })

  // Format planned travel time
  const plannedTravelTime = computed(() => {
    return formatDuration(travelTime.value * 2 * 60)
  })

  // Get the latest break start time
  const breakStartTime = computed(() => {
    const workDayPunches = punchStore.workToday?.punches || []
    const breakStartPunches = workDayPunches.filter((punch) => punch.type === 'BREAK_START' && punch.punched_at !== null)

    if (breakStartPunches.length === 0) return ''

    return breakStartPunches.reduce((latest, current) => {
      return !latest?.punched_at || new Date(current.punched_at) > new Date(latest.punched_at) ? current : latest
    }, breakStartPunches[0])?.punched_at
  })

  // Format a duration in seconds to a human-readable string (Xh Ymin Zs)
  function formatDuration(seconds: number, displaySeconds = false): string {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const remainingSeconds = seconds % 60

    let result = ''
    if (hours > 0) result += `${hours} h `
    if (minutes > 0 || hours > 0) result += `${minutes} min `
    if (displaySeconds && (remainingSeconds > 0 || (hours === 0 && minutes === 0))) result += `${remainingSeconds}s`

    return result.trim()
  }

  return {
    punchInTime,
    punchOutTime,
    breaks,
    totalBreakSeconds,
    paidBreakSeconds,
    unpaidBreakSeconds,
    totalBreakTime,
    rawWorkSeconds,
    billableWorkSeconds,
    rawWorkTime,
    billableWorkTime,
    travelTime,
    formatTravelTime,
    breakStartTime,
    isPaidBreak,
    plannedTravelTime,
    isPartiallyPaidBreak,
    formatDuration,
  }
}
